import React from "react";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import fixed_download_speed from "../../data/fixed_download_speed.json";
import { max } from "d3-array";
import FadeIn from "react-fade-in";
import { scaleLinear } from "d3-scale";

const margin = {
  top: 20,
  right: 50,
  bottom: 10,
  left: 50,
};

const Speed = ({
  data,
  title,
  section,
  note,
  source,
  sectionIndex,
  index = 0,
  id,
  highIncome,
  lowIncome,
  downloadSpeed,
  countriesLower,
  countryMap,
  ...rest
}) => {
  const { ref, width } = useVizualisation();

  const height = 800;

  const low2 = fixed_download_speed
    .filter((d) => d.income_group === "Low income")
    .sort((a, b) => b.speed_2019_mbps - a.speed_2019_mbps);
  const high2 = fixed_download_speed
    .filter((d) => d.income_group === "High income")
    .sort((a, b) => b.speed_2019_mbps - a.speed_2019_mbps);

  const w = Math.max(width, 500) - margin.left - margin.right;
  const h = height - margin.top - margin.bottom;

  const y = scaleLinear()
    .domain([0, max(fixed_download_speed, (d) => d.speed_2019_mbps)])
    .range([h - 50, 100])
    .nice();

  return (
    <Vizualisation note={note} source={source} ref={ref} size="large">
      <svg viewBox={`0 0 ${Math.max(width, 500)} 830`}>
        <defs>
          <pattern
            id="lines"
            height="4"
            width="4"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2"
              strokeWidth="1"
              stroke="#31989E"
              strokeLinecap="square"
            />
          </pattern>
        </defs>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <text
            x={w / 2}
            y={80}
            style={{
              fontSize: 14,
              fontWeight: "bold",
              textAnchor: "middle",
            }}
          >
            {downloadSpeed}
          </text>
          <rect
            x={w / 2 - 30}
            y={y.range()[1]}
            width={60}
            height={y.range()[0] - y(y.domain()[1] - 30)}
            fill="white"
          />
          <rect
            x={w / 2 - 30}
            y={y(30)}
            width={60}
            height={y.range()[0] - y(30)}
            fill="url(#lines)"
          />

          <line
            x1={w / 2 + 30}
            y1={y.range()[1]}
            x2={w / 2 + 30}
            y2={y.range()[0]}
            stroke="#000"
            style={{ strokeWidth: 1 }}
          />
          <line
            x1={w / 2 - 30}
            y1={y.range()[1]}
            x2={w / 2 - 30}
            y2={y.range()[0]}
            stroke="black"
            style={{ strokeWidth: 1 }}
          />
          {[0, 30, 60, 90, 120, 150, 180, 210].map((d) => (
            <g key={`tick-${d}`} transform={`translate(${w / 2}, ${y(d)})`}>
              <line
                x1={-30}
                x2={30}
                stroke="black"
                style={{ strokeWidth: 1, strokeDasharray: "2 4", opacity: 0.3 }}
              />
              <text
                style={{
                  textAnchor: "middle",
                  dominantBaseline: "central",
                  fontSize: 14,
                  fill: "rgba(255, 255, 255, 0.5)",
                  stroke: "rgba(255, 255, 255, 0.5)",
                  strokeWidth: 2,
                }}
              >
                {d}
              </text>
              <text
                style={{
                  textAnchor: "middle",
                  dominantBaseline: "central",
                  fontSize: 14,
                }}
              >
                {d}
              </text>
            </g>
          ))}
          <g className="low-income">
            <text>
              <tspan style={{ fontWeight: "bold" }}>{lowIncome}</tspan>{" "}
              {countriesLower}
            </text>
            <FadeIn wrapperTag="g" childTag="g">
              {low2.map((d, i) => (
                <g
                  transform={`translate(${w / 2 - 30}, 0)`}
                  key={`country-${d.country}`}
                >
                  <line
                    y1={y(d.speed_2019_mbps)}
                    x2={-w / 6}
                    y2={
                      -50 +
                      y.range()[1] +
                      (high2.length - low2.length) * 12 +
                      i * 12
                    }
                    style={{ strokeWidth: 1, strokeDasharray: "1 1" }}
                    stroke={d.speed_2019_mbps >= 30 ? "#EE6047" : "#31989E"}
                  />
                  <rect
                    width={6}
                    height={6}
                    x={-3}
                    y={y(d.speed_2019_mbps) - 3}
                    fill={d.speed_2019_mbps >= 30 ? "#EE6047" : "#31989E"}
                  />
                  <text
                    x={-w / 6}
                    y={
                      -50 +
                      y.range()[1] +
                      (high2.length - low2.length) * 12 +
                      i * 12
                    }
                    dx={-5}
                    style={{
                      dominantBaseline: "central",
                      fontSize: 13,
                      textAnchor: "end",
                      fontWeight: d.speed_2019_mbps >= 30 ? "bold" : "normal",
                    }}
                    fill={d.speed_2019_mbps >= 30 ? "#EE6047" : "#31989E"}
                  >
                    {countryMap[d.code]}
                  </text>
                </g>
              ))}
            </FadeIn>
          </g>
          <g className="high-income" style={{ textAnchor: "end" }}>
            <text x={w} className="subHeading">
              <tspan style={{ fontWeight: "bold" }}>{highIncome}</tspan>{" "}
              {countriesLower}
            </text>
            <FadeIn wrapperTag="g" childTag="g">
              {high2.map((d, i) => (
                <g
                  transform={`translate(${w / 2 + 30}, 0)`}
                  key={`country-${d.country}`}
                >
                  <line
                    y1={y(d.speed_2019_mbps)}
                    x2={w / 6}
                    y2={-50 + y.range()[1] + i * 12}
                    style={{ strokeWidth: 1, strokeDasharray: "1 1" }}
                    stroke={d.speed_2019_mbps >= 30 ? "#481F7C" : "#31989E"}
                  />
                  <rect
                    width={6}
                    height={6}
                    x={-3}
                    y={y(d.speed_2019_mbps) - 3}
                    fill={d.speed_2019_mbps >= 30 ? "#481F7C" : "#31989E"}
                  />
                  <text
                    x={w / 6}
                    y={-50 + y.range()[1] + i * 12}
                    dx={5}
                    style={{
                      dominantBaseline: "central",
                      fontSize: 13,
                      textAnchor: "start",
                      fontWeight: d.speed_2019_mbps >= 30 ? "bold" : "normal",
                    }}
                    fill={d.speed_2019_mbps >= 30 ? "#481F7C" : "#31989E"}
                  >
                    {countryMap[d.code]}
                  </text>
                </g>
              ))}
            </FadeIn>
          </g>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default Speed;
